import React, { useState } from 'react';
import { User, Award, Star, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AnimatedSection } from '../components/AnimatedSection';
import { trainers } from '../mock';

export const Trainers = () => {
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const whatsappNumber = '51939573726';
  const whatsappContactLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hola, estoy interesado')}`;
  
  return (
    <div className="min-h-screen bg-black pt-24 pb-20 px-4">
      <div className="container mx-auto">
        {/* Header */}
        <AnimatedSection animation="fade-zoom" className="text-center mb-16">
          <span className="px-4 py-2 bg-gradient-to-r from-blue-600/20 to-sky-500/20 border border-sky-500/30 rounded-full text-sky-400 text-sm font-semibold backdrop-blur-sm inline-block mb-6 animate-bounce" style={{ animationDuration: '2s' }}>
            NUESTRO EQUIPO
          </span>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
              Conoce a Nuestros
            </span>
            <br />
            <span className="bg-gradient-to-r from-blue-400 to-sky-400 bg-clip-text text-transparent">
              Entrenadores Expertos
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Profesionales certificados comprometidos con tu éxito y transformación
          </p>
        </AnimatedSection>

        {/* Trainers Grid - Responsive Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
          {trainers.map((trainer, index) => (
            <AnimatedSection
              key={trainer.id}
              animation={index % 2 === 0 ? "slide-right-strong" : "slide-left-strong"}
              delay={index * 0.12}
            >
            <Card
              className="bg-gradient-to-br from-gray-900 to-black border-sky-500/20 hover:border-sky-500/50 transition-all duration-500 hover:scale-110 hover:shadow-2xl hover:shadow-sky-500/50 group"
            >
              <CardContent className="p-6 text-center">
                {/* Circular Photo */}
                <div className="relative mb-6 flex justify-center">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-sky-500 rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500" style={{ transform: 'scale(0.9)' }} />
                  <div className="relative w-32 h-32 rounded-full overflow-hidden border-4 border-sky-500/50 group-hover:border-sky-400 transition-all duration-300 group-hover:scale-110">
                    <img 
                      src={trainer.photo} 
                      alt={trainer.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Trainer Info */}
                <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-sky-400 transition-colors duration-300">
                  {trainer.name}
                </h3>
                
                <p className="text-sky-400 font-semibold mb-1">{trainer.role}</p>
                
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-400 mb-4">
                  <Award className="h-4 w-4 text-yellow-500" />
                  <span>{trainer.specialty}</span>
                </div>

                <div className="flex items-center justify-center space-x-4 text-xs text-gray-500 mb-6">
                  <span>{trainer.experience}</span>
                  {trainer.age && (
                    <>
                      <span>•</span>
                      <span>{trainer.age}</span>
                    </>
                  )}
                </div>

                {/* Ver Perfil Button */}
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      className="w-full bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white font-semibold transition-all duration-300 group-hover:scale-105"
                      onClick={() => setSelectedTrainer(trainer)}
                    >
                      Ver Perfil
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  
                  {/* Profile Modal */}
                  <DialogContent className="bg-gradient-to-br from-gray-900 to-black border-sky-500/30 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-3xl font-bold text-center mb-4">
                        <span className="bg-gradient-to-r from-blue-400 to-sky-400 bg-clip-text text-transparent">
                          {trainer.name}
                        </span>
                      </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-6">
                      {/* Photo and Basic Info */}
                      <div className="flex flex-col items-center">
                        <div className="relative w-40 h-40 rounded-full overflow-hidden border-4 border-sky-500/50 mb-4">
                          <img 
                            src={trainer.photo} 
                            alt={trainer.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <p className="text-sky-400 font-semibold text-lg">{trainer.role}</p>
                        <p className="text-gray-400">{trainer.specialty}</p>
                      </div>

                      {/* Details */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-4">
                          <p className="text-sky-400 font-semibold text-sm mb-1">Experiencia</p>
                          <p className="text-white font-bold">{trainer.experience}</p>
                        </div>
                        {trainer.age && (
                          <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-4">
                            <p className="text-sky-400 font-semibold text-sm mb-1">Edad</p>
                            <p className="text-white font-bold">{trainer.age}</p>
                          </div>
                        )}
                      </div>

                      {/* Bio */}
                      <div className="bg-black/50 border border-sky-500/20 rounded-lg p-6">
                        <h4 className="text-sky-400 font-semibold mb-3 flex items-center">
                          <User className="h-5 w-5 mr-2" />
                          Sobre {trainer.name.split(' ')[0]}
                        </h4>
                        <p className="text-gray-300 leading-relaxed">{trainer.bio}</p>
                      </div>

                      {/* Certifications */}
                      {trainer.certifications && trainer.certifications.length > 0 && (
                        <div>
                          <h4 className="text-sky-400 font-semibold mb-3 flex items-center">
                            <Award className="h-5 w-5 mr-2" />
                            Certificaciones
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {trainer.certifications.map((cert, index) => (
                              <span
                                key={index}
                                className="px-4 py-2 bg-gradient-to-r from-blue-600/20 to-sky-500/20 border border-sky-500/30 rounded-full text-sky-400 text-sm font-medium"
                              >
                                {cert}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Personality */}
                      {trainer.personality && (
                        <div className="bg-gradient-to-r from-blue-600/10 to-sky-500/10 border border-sky-500/30 rounded-lg p-4">
                          <h4 className="text-sky-400 font-semibold mb-2 flex items-center">
                            <Star className="h-5 w-5 mr-2" />
                            Personalidad
                          </h4>
                          <p className="text-gray-300">{trainer.personality}</p>
                        </div>
                      )}

                      {/* Contact Button */}
                      <Button 
                        className="w-full bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-semibold py-6 text-lg transition-all duration-300 hover:scale-105"
                        onClick={() => window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hola, estoy interesado')}`, '_blank')}
                      >
                         Contactar por WhatsApp
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
            </AnimatedSection>
          ))}
        </div>

        {/* CTA Section */}
        <AnimatedSection animation="elastic-scale" delay={0.2}>
        <div className="text-center">
          <Card className="bg-gradient-to-br from-blue-600/20 to-sky-500/20 border-sky-500/30 max-w-3xl mx-auto backdrop-blur-sm hover:scale-105 transition-transform duration-300">
            <CardContent className="p-10">
              <h3 className="text-3xl font-bold text-white mb-4">
                ¿Quieres entrenar con nosotros?
              </h3>
              <p className="text-gray-300 mb-6 text-lg">
                Nuestros entrenadores están listos para ayudarte a alcanzar tus objetivos
              </p>
              <Button 
                className="bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white font-semibold px-8 py-4 transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-500/30"
                onClick={() => window.open(whatsappContactLink, '_blank')}
              >
                Reserva tu Sesión Gratuita
              </Button>
            </CardContent>
          </Card>
        </div>
        </AnimatedSection>
      </div>
    </div>
  );
};
